import {
    DEFAULT_CHAT_WELCOME_MESSAGES,
    EAgentModule,
    EChatAgentMessageAuthor,
    EChatAgentMessageEventType,
    type IAgentContext,
    type IChatAgentChat,
    type IChatAgentMessage,
    type IChatAgentMessageEvent,
    type TChatAgentEvent,
} from '@sharedInterfaces/ChatAgent';
import { EWebsocketTopics } from '@sharedInterfaces/globalEnums';
import { getLanguageTexts } from '@sharedInterfaces/Language/languageHelper';
import { WebSocketClient } from '@src/APIs/WebSockets/WebSocketClient';
import { addNewChat, setChatList, updateChatTitle } from '@store/reducer/chatAgentReducer';
import type { AppState } from '@store/store';
import { useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';

interface UseChatAgentOptions {
    autoInit?: boolean;
    dummyMessage?: string;
}

export const useChatAgent = (options: UseChatAgentOptions = {}) => {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const lang = useSelector((state: AppState) => state.employee.language);
    const langStrings = getLanguageTexts(lang).chatAgent;
    const {
        autoInit = false,
        dummyMessage = langStrings.howCanIHelp ||
            DEFAULT_CHAT_WELCOME_MESSAGES[lang] ||
            DEFAULT_CHAT_WELCOME_MESSAGES['en'],
    } = options;

    const [chat, setChat] = useState<IChatAgentChat | null>(null);
    const [messages, setMessages] = useState<IChatAgentMessage[]>([]);
    const [isTyping, setIsTyping] = useState(false);
    const [isInitialized, setIsInitialized] = useState(false);
    const [pendingMessage, setPendingMessage] = useState<string | null>(null);
    const [pendingAgentContext, setPendingAgentContext] = useState<IAgentContext | undefined>(undefined);

    const subscriptionRef = useRef<string | null>(null);
    const chatRef = useRef<IChatAgentChat | null>(null);
    const pendingMessageRef = useRef<string | null>(null);
    const pendingAgentContextRef = useRef<IAgentContext | undefined>(undefined);
    const topic = EWebsocketTopics.CHAT_AGENT;

    // Keep refs in sync
    useEffect(() => {
        chatRef.current = chat;
    }, [chat]);

    useEffect(() => {
        pendingMessageRef.current = pendingMessage;
    }, [pendingMessage]);

    useEffect(() => {
        pendingAgentContextRef.current = pendingAgentContext;
    }, [pendingAgentContext]);

    // Initialize with dummy message
    useEffect(() => {
        if (dummyMessage && messages.length === 0) {
            const dummyMsg: IChatAgentMessage = {
                role: EChatAgentMessageAuthor.AI,
                text: dummyMessage,
            };
            setMessages([dummyMsg]);
        }
    }, [dummyMessage]);

    // Initialize chat when first message is sent or autoInit is true
    const initializeChat = () => {
        if (isInitialized || chat) return;

        setIsInitialized(true);
        WebSocketClient.sendMessage(topic, { type: EChatAgentMessageEventType.START_CHAT });
    };

    // Check for chat restoration after navigation
    useEffect(() => {
        const navigationStateStr = localStorage.getItem('t4lo_chat_navigation');
        if (navigationStateStr) {
            try {
                const navigationState = JSON.parse(navigationStateStr);
                const now = Date.now();
                
                // Prüfe TTL
                if (now - navigationState.timestamp < navigationState.ttl) {
                    console.log('🔄 Restoring chat after navigation:', navigationState.chatId);
                    // Chat ist noch gültig - wiederherstellen
                    loadChat(navigationState.chatId);
                } else {
                    console.log('⏰ Chat restoration expired (TTL exceeded)');
                }
                
                // Cleanup: Entferne State aus localStorage
                localStorage.removeItem('t4lo_chat_navigation');
            } catch (error) {
                console.error('Failed to restore chat after navigation:', error);
                localStorage.removeItem('t4lo_chat_navigation');
            }
        }
    }, []); // Nur beim ersten Mount

    // WebSocket subscription
    useEffect(() => {
        if (!isInitialized && autoInit) {
            initializeChat();
        }

        const subKey = WebSocketClient.subscribe<TChatAgentEvent>(topic, (data: TChatAgentEvent) => {
            if (data.error) {
                const errorMsg: IChatAgentMessage = {
                    role: EChatAgentMessageAuthor.AI,
                    text: data.error,
                };
                addMessageToHistory(errorMsg);
                setIsTyping(false);
                return;
            }

            if (data.type === EChatAgentMessageEventType.NAVIGATE_TO) {
                // Client-Navigation durch AI-Agent
                console.log('AI Agent navigation request:', data.path);
                
                // Speichere Chat-State für Wiederherstellung nach Navigation
                if (chatRef.current?.chatId) {
                    const navigationState = {
                        chatId: chatRef.current.chatId,
                        currentModule: chatRef.current.currentModule || 'general', // Speichere aktuelles Modul
                        timestamp: Date.now(),
                        ttl: 60000, // 60 Sekunden
                    };
                    localStorage.setItem('t4lo_chat_navigation', JSON.stringify(navigationState));
                    console.log('💾 Chat state saved for navigation:', navigationState);
                }
                
                navigate(data.path);
                return;
            }

            if (data.type === EChatAgentMessageEventType.INIT_CHAT) {
                console.log('Chat loaded/initialized');
                setChat(data.chat);
                setIsInitialized(true);

                // Add new chat to chat list in store (reducer prevents duplicates)
                const messageKeys = Object.keys(data.chat.messages || {}).sort();
                const lastMessageAt =
                    messageKeys.length > 0 ? messageKeys[messageKeys.length - 1] : data.chat.createdAt;
                const chatMetadata = {
                    chatId: data.chat.chatId,
                    title: data.chat.title || 'Neuer Chat',
                    createdAt: data.chat.createdAt,
                    lastMessageAt,
                };
                dispatch(addNewChat(chatMetadata)); // Add new chat to beginning of list

                // Load all messages from the chat first
                const chatMessages = Object.values(data.chat.messages) as IChatAgentMessage[];
                if (chatMessages.length > 0) {
                    setMessages(chatMessages);
                } else if (dummyMessage) {
                    const dummyMsg: IChatAgentMessage = {
                        role: EChatAgentMessageAuthor.AI,
                        text: dummyMessage,
                    };
                    setMessages([dummyMsg]);
                }

                // Send pending message if exists (after messages are loaded)
                if (pendingMessageRef.current) {
                    const newEvent: IChatAgentMessageEvent = {
                        type: EChatAgentMessageEventType.USER_QUESTION,
                        chatId: data.chat.chatId,
                        message: pendingMessageRef.current,
                        agentContext: pendingAgentContextRef.current,
                    };
                    WebSocketClient.sendMessage(EWebsocketTopics.CHAT_AGENT, newEvent);
                    setPendingMessage(null);
                    setPendingAgentContext(undefined);
                }
            } else if (data.type === EChatAgentMessageEventType.AI_ANSWER && data.chatId === chatRef.current?.chatId) {
                const answerMsg: IChatAgentMessage = {
                    role: EChatAgentMessageAuthor.AI,
                    text: data.message,
                };
                addMessageToHistory(answerMsg);
                setIsTyping(false);
            } else if (
                data.type === EChatAgentMessageEventType.CHAT_TITLE_GENERATED &&
                data.chatId === chatRef.current?.chatId
            ) {
                dispatch(updateChatTitle({ chatId: data.chatId, title: data.title }));
            } else if (data.type === EChatAgentMessageEventType.CHAT_LIST_RESPONSE) {
                dispatch(setChatList(data.chats));
            }
        });

        subscriptionRef.current = subKey;

        return () => {
            if (subscriptionRef.current) {
                WebSocketClient.unsubscribe(subscriptionRef.current);
                subscriptionRef.current = null;
            }
        };
    }, [isInitialized, autoInit, dummyMessage, dispatch, navigate]);

    const addMessageToHistory = (msg: IChatAgentMessage) => {
        setMessages((prev) => [...prev, msg]);
    };

    const sendMessage = async (message: string, agentContext?: IAgentContext) => {
        const trimmedInput = message.trim();
        if (!trimmedInput) return false;

        console.log('sendMessage called with:', trimmedInput);
        console.log('isInitialized:', isInitialized);
        console.log('chat:', chat);

        // Add current URL to agentContext
        const contextWithUrl: IAgentContext = {
            ...agentContext,
            module: agentContext?.module || EAgentModule.GENERAL,
            currentUrl: window.location.href,
        };

        // Add user message to history immediately
        const userMsg: IChatAgentMessage = {
            role: EChatAgentMessageAuthor.USER,
            text: trimmedInput,
        };
        addMessageToHistory(userMsg);
        setIsTyping(true);

        // Initialize chat if not already done
        if (!isInitialized) {
            console.log('Initializing new chat...');
            initializeChat();
            // Store the message and context to send after initialization
            setPendingMessage(trimmedInput);
            setPendingAgentContext(contextWithUrl);
            return true;
        }

        if (!chat) {
            console.log('Chat not ready, storing as pending...');
            // Chat is initializing, store message and context as pending
            setPendingMessage(trimmedInput);
            setPendingAgentContext(contextWithUrl);
            return true;
        }

        const newEvent: IChatAgentMessageEvent = {
            type: EChatAgentMessageEventType.USER_QUESTION,
            chatId: chat.chatId,
            message: trimmedInput,
            agentContext: contextWithUrl,
        };

        console.log('Sending WebSocket message:', newEvent);
        WebSocketClient.sendMessage(EWebsocketTopics.CHAT_AGENT, newEvent);
        return true;
    };

    const resetChat = () => {
        setMessages([]);
        setChat(null);
        setIsInitialized(false);
        setIsTyping(false);
        setPendingMessage(null);
        setPendingAgentContext(undefined);

        // Add dummy message back
        if (dummyMessage) {
            const dummyMsg: IChatAgentMessage = {
                role: EChatAgentMessageAuthor.AI,
                text: dummyMessage,
            };
            setMessages([dummyMsg]);
        }
    };

    const getChatList = () => {
        WebSocketClient.sendMessage(EWebsocketTopics.CHAT_AGENT, {
            type: EChatAgentMessageEventType.GET_CHAT_LIST,
        });
    };

    const loadChat = (chatId: string) => {
        WebSocketClient.sendMessage(EWebsocketTopics.CHAT_AGENT, {
            type: EChatAgentMessageEventType.LOAD_CHAT,
            chatId: chatId,
        });
    };

    return {
        chat,
        messages,
        isTyping,
        isInitialized,
        sendMessage,
        resetChat,
        addMessageToHistory,
        getChatList,
        loadChat,
    };
};
